/**
 * Role-based access control hook.
 * Returns the user's role for the active entity and permission checks.
 */

import { useAppStore } from '@/stores/useAppStore';
import type { UserRole } from '@/types';

// Role hierarchy: platform_admin > owner > admin > bookkeeper > accountant > viewer > manager > employee
//
// employee + manager (S25/S26) sit at the bottom. They have NO access to
// owner-side routes (Dashboard, Capture, Reports, Tax, etc.) - that's
// enforced by the AppShell redirect that sends them to /employee instead.
// `hasMinRole('viewer')` returns FALSE for an employee so role-gated
// surfaces stay closed even if the employee somehow lands on a non-/employee
// path. The server is the security boundary; this is UX gating.
const ROLE_LEVEL: Record<UserRole, number> = {
  platform_admin: 100,
  owner: 80,
  admin: 60,
  bookkeeper: 40,
  accountant: 30,
  viewer: 10,
  manager: 5,
  employee: 1,
};

export function useRole() {
  const { user, activeEntity, entityMemberships } = useAppStore();

  const membership = entityMemberships.find(
    (m) => m.entityId === activeEntity?.id && m.userId === user?.id && m.isActive
  );

  const role: UserRole = user?.isPlatformAdmin ? 'platform_admin' : (membership?.role || 'viewer');
  const level = ROLE_LEVEL[role] || 0;

  return {
    role,
    isPlatformAdmin: user?.isPlatformAdmin || false,
    isOwner: level >= ROLE_LEVEL.owner,
    isAdmin: level >= ROLE_LEVEL.admin,
    isEmployee: role === 'employee',
    isManager: role === 'manager',
    canEditExpenses: level >= ROLE_LEVEL.bookkeeper,
    canViewReports: level >= ROLE_LEVEL.accountant,
    canExportPayroll: level >= ROLE_LEVEL.admin,
    canManageSettings: level >= ROLE_LEVEL.owner,
    hasMinRole: (minRole: UserRole) => level >= ROLE_LEVEL[minRole],
  };
}
